import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useSession } from '@/lib/auth-client';
import { permissionService } from '@/services/permission-service';

interface PermissionContextType {
  isAdmin: boolean | null; // null indicates loading
  isInitialized: boolean; // Whether permissions have been loaded
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
  children: ReactNode;
}

/**
 * PermissionProvider manages the global permission state
 * It loads permissions once when the user session is established
 * and updates when the session changes
 */
const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const { data: session, isPending: sessionPending } = useSession();

  const refreshPermissions = async () => {
    // If no user session, set permissions to false
    if (!session?.user?.id) {
      setIsAdmin(false);
      setIsInitialized(true);
      return;
    }

    try {
      // Load admin status in one call
      const adminStatus = await permissionService.isAdmin();
      setIsAdmin(adminStatus);
    } catch (error) {
      console.error('Error loading permissions:', error);
      setIsAdmin(false);
    } finally {
      setIsInitialized(true);
    }
  };

  // Load permissions when session changes
  useEffect(() => {
    // Only refresh if we have a session and it's not pending
    if (!sessionPending) {
      refreshPermissions();
    } else {
      // If session is pending, set to loading state
      setIsAdmin(null);
      setIsInitialized(false);
    }
  }, [session, sessionPending]);

  const contextValue: PermissionContextType = {
    isAdmin,
    isInitialized,
    refreshPermissions,
  };

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

// Custom hook to use the Permission context
const usePermissionContext = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissionContext must be used within a PermissionProvider');
  }
  return context;
};

// Custom hook that combines context state with permission service for ad-hoc checks
const usePermission = () => {
  const context = usePermissionContext();
  
  return {
    ...context,
    // Allow for specific permission checks beyond admin status
    hasPermission: permissionService.hasPermission,
    can: permissionService.can,
    hasMultiplePermissions: permissionService.hasMultiplePermissions,
  };
};

export { PermissionProvider, usePermissionContext, usePermission };